import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import StarIcon from '@material-ui/icons/Star';
import ConnectIcon from '@material-ui/icons/DeviceHub';
import FirewallIcon from '@material-ui/icons/Lock';
import UsersIcon from '@material-ui/icons/PersonAdd';
import SettingsIcon from '@material-ui/icons/Settings';
import DatabasesIcon from '@material-ui/icons/Storage';
import RegionsIcon from '@material-ui/icons/Language';
import BackupsIcon from '@material-ui/icons/SettingsBackupRestore';
import NodesIcon from '@material-ui/icons/Grain';
import Poll from 'components/Poll/Poll';
import { uiState } from 'react-redux-ui-state';

import LoadingPlaceHolder from 'components/LoadingPlaceholder/LoadingPlaceholder';
import ORContentHeader from 'components/ORContentHeader/ORContentHeader';
import ORContentArea from 'components/ORContentArea/ORContentArea';
import SERVICES_WITH_USERS from 'constants/servicesWithUsers';
import SERVICES_WITH_DATABASES from 'constants/servicesWithDatabases';
import InstanceDetailOverview from './Overview/Overview';
import InstanceConnectTab from './ConnectTab/ConnectTabContainer';
import InstanceUsersTab from './UsersTab/UsersTab';
import InstanceSettingsTab from './SettingsTab/SettingsTab';
import InstanceAclTab from './AclTab/AclTab';
import InstanceDatabasesTab from './DatabasesTab/DatabasesTab';
import InstanceBackupsTab from './BackupsTab/BackupsTab';
import InstanceNodesTab from './NodesTab/NodesTabContainer';
import InstanceRegionsTab from './RegionsTab/RegionsTabContainer';

const propTypes = {
  instances: PropTypes.PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  match: PropTypes.shape({}).isRequired,
  tabSelectionValue: PropTypes.string.isRequired,
  setUiState: PropTypes.func.isRequired,
  deleteInstance: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  getInstancePoll: PropTypes.func.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  flags: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  getRedisMemoryCapacity: PropTypes.func.isRequired,
};

export function UnstyledInstanceDetail(props) {
  const {
    flags,
    classes,
    isLoading,
    instances,
    match,
    tabSelectionValue,
    setUiState,
    deleteInstance,
    history,
    userRoles,
    getInstancePoll,
    getRedisMemoryCapacity,
  } = props;

  const {
    params: { instanceId },
  } = match;

  const changeTabSelection = (event, value) => {
    setUiState({
      tabSelectionValue: value,
    });
  };

  if (isLoading || !instances[instanceId]) {
    return <LoadingPlaceHolder />;
  }

  let currentDisplayedTab = null;

  const instance = instances[instanceId];

  const disabled = instance.statusDisplay !== 'Ready';

  const instanceService = instances[instanceId].service.toLowerCase();

  if (tabSelectionValue === 'overview') {
    currentDisplayedTab = (
      <React.Fragment>
        <Poll
          intervalInMS={15000}
          actionCreator={() => getInstancePoll(instanceId, instanceService)}
        />
        <InstanceDetailOverview
          {...instance}
          userRoles={userRoles}
          getRedisMemoryCapacity={getRedisMemoryCapacity}
        />
      </React.Fragment>
    );
  }
  if (tabSelectionValue === 'connect') {
    currentDisplayedTab = <InstanceConnectTab {...instance} />;
  } else if (tabSelectionValue === 'firewall') {
    currentDisplayedTab = <InstanceAclTab {...instance} />;
  } else if (tabSelectionValue === 'users') {
    currentDisplayedTab = <InstanceUsersTab {...instance} />;
  } else if (tabSelectionValue === 'settings') {
    currentDisplayedTab = (
      <InstanceSettingsTab
        {...instance}
        deleteInstance={deleteInstance}
        history={history}
        userRoles={userRoles}
      />
    );
  } else if (tabSelectionValue === 'databases') {
    currentDisplayedTab = <InstanceDatabasesTab {...instance} />;
  } else if (tabSelectionValue === 'backups') {
    currentDisplayedTab = <InstanceBackupsTab {...instance} />;
  } else if (tabSelectionValue === 'nodes') {
    currentDisplayedTab = (
      <React.Fragment>
        <Poll
          intervalInMS={15000}
          actionCreator={() => getInstancePoll(instanceId, instanceService)}
        />
        <InstanceNodesTab {...instance} userRoles={userRoles} />
      </React.Fragment>
    );
  } else if (tabSelectionValue === 'regions') {
    currentDisplayedTab = <InstanceRegionsTab {...instance} />;
  }

  return (
    <React.Fragment>
      <div className={classes.details}>
        <ORContentHeader hasDivider>{instance.name}</ORContentHeader>
        <ORContentArea>
          <div className={classes.tabsContainer}>
            <Tabs
              value={tabSelectionValue}
              onChange={changeTabSelection}
              variant="scrollable"
              scrollButtons="auto"
              classes={{
                root: classes.tabsRoot,
                indicator: classes.tabsIndicator,
              }}
            >
              <Tab
                value="overview"
                label="Overview"
                icon={<StarIcon />}
                className={classes.detailsTab}
                classes={{ selected: classes.tabSelected, labelIcon: classes.labelIcon }}
              />
              <Tab
                value="connect"
                disabled={disabled}
                label="Connect"
                icon={<ConnectIcon />}
                className={classes.detailsTab}
                classes={{
                  selected: classes.tabSelected,
                  disabled: classes.disabledTab,
                  labelIcon: classes.labelIcon,
                }}
              />
              <Tab
                value="firewall"
                disabled={disabled}
                label="FIREWALL"
                icon={<FirewallIcon />}
                className={classes.detailsTab}
                classes={{
                  root: classes.firewallTab,
                  selected: classes.tabSelected,
                  disabled: classes.disabledTab,
                  labelIcon: classes.labelIcon,
                }}
              />

              {instanceService === 'cockroachdb' &&
                typeof instance.metadata.multiInstanceId !== 'undefined' && (
                  <Tab
                    value="regions"
                    disabled={disabled}
                    label="Regions"
                    icon={<RegionsIcon />}
                    className={classes.detailsTab}
                    classes={{
                      selected: classes.tabSelected,
                      disabled: classes.disabledTab,
                      labelIcon: classes.labelIcon,
                    }}
                  />
                )}

              {SERVICES_WITH_USERS.includes(instance.service) && (
                <Tab
                  value="users"
                  disabled={disabled}
                  label="Users"
                  icon={<UsersIcon />}
                  className={classes.detailsTab}
                  classes={{
                    selected: classes.tabSelected,
                    disabled: classes.disabledTab,
                    labelIcon: classes.labelIcon,
                  }}
                />
              )}
              {SERVICES_WITH_DATABASES.includes(instance.service) && (
                <Tab
                  value="databases"
                  disabled={disabled}
                  label="Databases"
                  icon={<DatabasesIcon />}
                  className={classes.detailsTab}
                  classes={{
                    selected: classes.tabSelected,
                    disabled: classes.disabledTab,
                    labelIcon: classes.labelIcon,
                  }}
                />
              )}
              <Tab
                value="settings"
                label="Settings"
                icon={<SettingsIcon />}
                className={classes.detailsTab}
                classes={{ selected: classes.tabSelected, labelIcon: classes.labelIcon }}
              />
              <Tab
                value="backups"
                label="Backups"
                icon={<BackupsIcon />}
                className={classes.detailsTab}
                classes={{ selected: classes.tabSelected, labelIcon: classes.labelIcon }}
              />
              {flags &&
                flags['nodes-tab'] && ( // eslint-disable-line     react/prop-types
                  <Tab
                    value="nodes"
                    disabled={
                      disabled &&
                      instance.statusDisplay !== 'Update Requested' &&
                      instance.statusDisplay !== 'Updating'
                    }
                    label="Nodes"
                    icon={<NodesIcon />}
                    className={classes.detailsTab}
                    classes={{
                      selected: classes.tabSelected,
                      disabled: classes.disabledTab,
                      labelIcon: classes.labelIcon,
                    }}
                  />
                )}
            </Tabs>
          </div>
          <div className={classes.detailArea}>{currentDisplayedTab}</div>
        </ORContentArea>
      </div>
    </React.Fragment>
  );
}

UnstyledInstanceDetail.propTypes = propTypes;

const styles = (theme) => ({
  details: {
    color: theme.customColors.black,
  },
  tabsContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.customColors.teal}`,
  },
  detailsTab: {
    color: theme.customColors.teal,
    minWidth: 'unset',
    fontSize: '0.857rem',
    paddingBottom: 0,
    paddingRight: theme.spacing(3.5),
    paddingLeft: theme.spacing(3.5),
    fontWeight: 'bold',
  },
  firewallTab: {
    textTransform: 'none',
  },
  disabledTab: {
    color: '#6C6D6D',
  },
  noTab: {
    display: 'none',
  },
  detailArea: {
    // One day we'll be asked to add a 1px grey border here, so keeping this part for now
    borderRadius: '3px',
  },

  // M.UI API classes for Tabs container
  tabsRoot: {
    marginBottom: -2,
  },
  tabsIndicator: {
    backgroundColor: theme.customColors.orange,
  },

  // M.UI API classes for individual Tabs
  tabSelected: {
    color: theme.customColors.orange,
  },
  labelIcon: {
    minHeight: 'unset',
  },
});

export const uiConfig = {
  name: 'tabSelectionValue',
  state: () => ({
    tabSelectionValue: 'overview',
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledInstanceDetail));
